import { useMemo } from "react";

import { twMerge } from "@/utils/twMerge";

import { SvgGenerator } from "./svgUtils/SvgGenerator";

import type { Path } from "./svgUtils/SvgGenerator";

export type Corner = "top-left" | "top-right" | "bottom-right" | "bottom-left";

const STROKE_WIDTH = 1;
/** Half a stroke of inset keeps the line on the pixel grid instead of straddling the edge. */
const INSET = STROKE_WIDTH / 2;
const RADIUS = 3.67;
/** How far an arm reaches along each edge, measured from the box corner. */
const ARM = 5;

// The arcs keep their size at any box size — only the corner they hang off moves, so
// every coordinate is a fixed offset from one of the four edges.
const NEAR = INSET;
const NEAR_ARC = INSET + RADIUS;
const NEAR_ARM = INSET + ARM;
const ARC = `A ${RADIUS} ${RADIUS} 0 0`;

const CORNERS: Record<Corner, Path> = {
  "top-left": [
    "stroke",
    `M {0w+${NEAR_ARM}} {0h+${NEAR}} H {0w+${NEAR_ARC}} ${ARC} 0 {0w+${NEAR}} {0h+${NEAR_ARC}} V {0h+${NEAR_ARM}}`,
  ],
  "top-right": [
    "stroke",
    `M {100w-${NEAR_ARM}} {0h+${NEAR}} H {100w-${NEAR_ARC}} ${ARC} 1 {100w-${NEAR}} {0h+${NEAR_ARC}} V {0h+${NEAR_ARM}}`,
  ],
  "bottom-right": [
    "stroke",
    `M {100w-${NEAR_ARM}} {100h-${NEAR}} H {100w-${NEAR_ARC}} ${ARC} 0 {100w-${NEAR}} {100h-${NEAR_ARC}} V {100h-${NEAR_ARM}}`,
  ],
  "bottom-left": [
    "stroke",
    `M {0w+${NEAR_ARM}} {100h-${NEAR}} H {0w+${NEAR_ARC}} ${ARC} 1 {0w+${NEAR}} {100h-${NEAR_ARC}} V {100h-${NEAR_ARM}}`,
  ],
};

export function CorneredBox({ className, exclude }: { className?: string; exclude?: Corner[] }) {
  const paths = useMemo(() => {
    if (!exclude) return Object.values(CORNERS);
    return Object.entries(CORNERS)
      .filter(([corner]) => !exclude.includes(corner as Corner))
      .map(([, path]) => path);
  }, [exclude]);

  return (
    <div className={twMerge("pointer-events-none absolute inset-0", className)}>
      <SvgGenerator paths={paths} strokeWidth={STROKE_WIDTH} />
    </div>
  );
}
